import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchInput } from '../components/ui/SearchInput';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import { useStaffList } from '../lib/options';
import { useDebounced } from '../lib/useDebounced';
import { Loader } from '../components/ui/Spinner';

type AuditLog = { id: string; actorUserId: string | null; action: string; entityType: string; entityId: string | null; before: unknown; after: unknown; ipAddress: string | null; createdAt: string };

const ACTION_STYLE: Record<string, string> = { create: 'bg-brand-100 text-brand-700', delete: 'bg-red-50 text-red-700', deactivate: 'bg-red-50 text-red-700', update: 'bg-amber-100 text-amber-700' };

function ActionBadge({ action }: { action: string }) {
  const style = ACTION_STYLE[action] ?? Object.entries(ACTION_STYLE).find(([key]) => action.includes(key))?.[1] ?? 'bg-slate-100 text-slate-700';
  return <span className={`badge ${style}`}>{action.replace(/_/g, ' ')}</span>;
}

export function AuditLogPage() {
  const [entityType, setEntityType] = useState('');
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const staff = useStaffList();

  // The entity-type dropdown is built from a small unfiltered sample so its options don't
  // shrink as the search narrows the list below.
  const typesQuery = useQuery({ queryKey: ['audit-logs', 'types'], queryFn: async () => (await api.get<AuditLog[]>('/api/audit-logs')).data });
  const entityTypes = useMemo(() => [...new Set((typesQuery.data ?? []).map((l) => l.entityType))].sort(), [typesQuery.data]);

  const logs = useQuery({
    queryKey: ['audit-logs', { entityType, q: debouncedQ, from, to }],
    queryFn: async () => (await api.get<AuditLog[]>('/api/audit-logs', { params: { entityType: entityType || undefined, q: debouncedQ || undefined, from: from || undefined, to: to || undefined } })).data,
  });
  const actorName = (id: string | null) => (id ? staff.data?.find((s) => s.id === id)?.name ?? id : 'System');

  return (
    <>
      <div className="mb-6"><h1 className="text-3xl font-bold">Audit log</h1><p className="mt-1 text-muted">Who changed what, and when.</p></div>
      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <SearchInput className="sm:col-span-2" placeholder="Search by person or action…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={entityType} onChange={(e) => setEntityType(e.target.value)}><option value="">All entity types</option>{entityTypes.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}</Select>
        <div className="grid grid-cols-2 gap-2">
          <input type="date" className="field" aria-label="From date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" className="field" aria-label="To date" min={from || undefined} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {logs.isLoading && <Loader />}
      {!logs.isLoading && logs.data?.length === 0 && <EmptyState title="No matching activity" />}
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Action</th><th>Entity</th><th>By</th><th>When</th></tr></thead>
          <tbody>
            {logs.data?.map((log) => (
              <tr key={log.id}>
                <td><ActionBadge action={log.action} /></td>
                <td className="text-muted">{log.entityType.replace(/_/g, ' ')}</td>
                <td>{actorName(log.actorUserId)}{log.ipAddress ? <span className="text-muted"> • {log.ipAddress}</span> : null}</td>
                <td className="text-muted">{new Date(log.createdAt).toLocaleString('en-IN')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
